import {
  summarizeWxLoginIframeSrc,
  type WxLoginIframeSummary,
} from './reportWeChatAuthFailure'

const WX_LOGIN_MOUNT_TIMEOUT_MS = 10_000
const WX_LOGIN_POST_LOAD_CHECK_MS = 2_000

const WX_LOGIN_WIDGET_ERROR_SNIPPETS = [
  'Oops',
  'something went wrong',
  '出了点问题',
]

export type WxLoginQrMountFailureReason =
  | 'container_not_found'
  | 'iframe_mount_timeout'
  | 'widget_error_text'

export class WxLoginQrMountError extends Error {
  readonly reason: WxLoginQrMountFailureReason
  readonly iframeSrcSummary?: WxLoginIframeSummary

  constructor(input: {
    reason: WxLoginQrMountFailureReason
    message: string
    iframeSrcSummary?: WxLoginIframeSummary
  }) {
    super(input.message)
    this.name = 'WxLoginQrMountError'
    this.reason = input.reason
    this.iframeSrcSummary = input.iframeSrcSummary
  }
}

function containerShowsWidgetError(container: HTMLElement): boolean {
  const text = container.innerText
  for (const snippet of WX_LOGIN_WIDGET_ERROR_SNIPPETS) {
    if (text.includes(snippet)) {
      return true
    }
  }
  return false
}

function getIframeSummary(
  container: HTMLElement
): WxLoginIframeSummary | undefined {
  const iframe = container.querySelector('iframe')
  if (!iframe?.src) {
    return undefined
  }
  return summarizeWxLoginIframeSrc(iframe.src)
}

export function waitForWxLoginQrMount(containerId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const container = document.getElementById(containerId)
    if (!container) {
      reject(
        new WxLoginQrMountError({
          reason: 'container_not_found',
          message: 'WeChat QR container element was not found',
        })
      )
      return
    }

    let settled = false
    let postLoadCheckTimeoutId: ReturnType<typeof setTimeout> | undefined
    let mountTimeoutId: ReturnType<typeof setTimeout> | undefined

    const finishResolve = () => {
      if (settled) {
        return
      }
      settled = true
      observer.disconnect()
      if (mountTimeoutId) {
        clearTimeout(mountTimeoutId)
      }
      if (postLoadCheckTimeoutId) {
        clearTimeout(postLoadCheckTimeoutId)
      }
      resolve()
    }

    const finishReject = (error: WxLoginQrMountError) => {
      if (settled) {
        return
      }
      settled = true
      observer.disconnect()
      if (mountTimeoutId) {
        clearTimeout(mountTimeoutId)
      }
      if (postLoadCheckTimeoutId) {
        clearTimeout(postLoadCheckTimeoutId)
      }
      reject(error)
    }

    const checkContainer = () => {
      if (containerShowsWidgetError(container)) {
        finishReject(
          new WxLoginQrMountError({
            reason: 'widget_error_text',
            message:
              'WeChat QR widget displayed an error (often caused by redirect_uri or appId misconfiguration)',
            iframeSrcSummary: getIframeSummary(container),
          })
        )
        return true
      }

      const iframe = container.querySelector('iframe')
      if (!iframe) {
        return false
      }

      const iframeSrcSummary = getIframeSummary(container)
      const runPostLoadCheck = () => {
        if (postLoadCheckTimeoutId) {
          return
        }
        postLoadCheckTimeoutId = setTimeout(() => {
          if (containerShowsWidgetError(container)) {
            finishReject(
              new WxLoginQrMountError({
                reason: 'widget_error_text',
                message:
                  'WeChat QR widget displayed an error after iframe load',
                iframeSrcSummary,
              })
            )
            return
          }
          finishResolve()
        }, WX_LOGIN_POST_LOAD_CHECK_MS)
      }

      if (iframe.dataset.wxLoginLoadBound !== 'true') {
        iframe.dataset.wxLoginLoadBound = 'true'
        iframe.addEventListener('load', runPostLoadCheck)
      }

      if (iframe.src) {
        runPostLoadCheck()
      }

      return false
    }

    if (checkContainer()) {
      return
    }

    const observer = new MutationObserver(() => {
      checkContainer()
    })
    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    })

    mountTimeoutId = setTimeout(() => {
      const iframeSrcSummary = getIframeSummary(container)
      if (!container.querySelector('iframe')) {
        finishReject(
          new WxLoginQrMountError({
            reason: 'iframe_mount_timeout',
            message: 'WeChat QR iframe did not mount within the timeout',
            iframeSrcSummary,
          })
        )
        return
      }
      finishResolve()
    }, WX_LOGIN_MOUNT_TIMEOUT_MS)
  })
}
